import { useEffect, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { usePermission } from '@/shell/module-system'
import { ArrowLeft, Download, FileText, Loader2, Plus, Save, Send, Trash2 } from 'lucide-react'
import {
  Button,
  Checkbox,
  Input,
  Textarea,
  Toggle,
  toast,
} from '@/components/ui'
import { ApiError } from '@/lib/api/jsonFetch'
import {
  useCreateIncident,
  useDeleteIncident,
  useGeneratePdf,
  useIncident,
  useLinkDiscrepancyReport,
  useUnlinkDiscrepancyReport,
  useUpdateIncident,
} from '../api/queries'
import type { DamagedItem, IncidentReport, IncidentType, PersonInvolved } from '../api/types'
import { INCIDENT_TYPE_LABELS } from '../api/types'
import { DamagedItemsEditor } from '../components/DamagedItemsEditor'
import { IncidentStatusChip } from '../components/IncidentStatusChip'
import { SignOffPanel } from '../components/SignOffPanel'

// ─── Section wrapper ────────────────────────────────────────────────

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="bg-[#1A3A5C] text-white text-sm font-bold px-3 py-2 rounded-t">
      {label}
    </div>
  )
}

function SectionBody({ children }: { children: React.ReactNode }) {
  return (
    <div className="border border-border-default border-t-0 rounded-b p-4 space-y-4">
      {children}
    </div>
  )
}

function FieldGroup({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">{children}</div>
}

function Field({
  label,
  children,
  colSpan = 1,
}: {
  label: string
  children: React.ReactNode
  colSpan?: 1 | 2 | 3
}) {
  const spanClass = colSpan === 3 ? 'sm:col-span-3' : colSpan === 2 ? 'sm:col-span-2' : ''
  return (
    <div className={`space-y-1 ${spanClass}`}>
      <label className="text-xs text-secondary uppercase tracking-wide font-medium">
        {label}
      </label>
      {children}
    </div>
  )
}

// ─── Form state ─────────────────────────────────────────────────────

interface FormState {
  date_of_report: string
  facility_site: string
  date_of_incident: string
  time_of_incident: string
  reported_to_mgmt_date: string
  location_in_warehouse: string
  incident_types: IncidentType[]
  persons_involved: PersonInvolved[]
  witnesses: string
  reported_by_name: string
  department_shift: string
  equipment_items: DamagedItem[]
  material_items: DamagedItem[]
  incident_description: string
  cause_factors: string
  immediate_actions: string
  corrective_actions: string
  injuries: boolean
  medical_details: string
}

const _blankEquipmentItem = (): DamagedItem => ({
  category: 'equipment', description: '', asset_id: '', product_code: '',
  lot_serial: '', quantity_affected: '', unit_of_measure: '',
  estimated_damage_value: '', repairable: '' as const,
})

const _blankMaterialItem = (): DamagedItem => ({
  category: 'material', description: '', asset_id: '', product_code: '',
  lot_serial: '', quantity_affected: '', unit_of_measure: '',
  estimated_damage_value: '', repairable: '' as const,
})

function blankForm(): FormState {
  return {
    date_of_report: new Date().toISOString().slice(0, 10),
    facility_site: '',
    date_of_incident: '',
    time_of_incident: '',
    reported_to_mgmt_date: '',
    location_in_warehouse: '',
    incident_types: [],
    persons_involved: [{ name: '', role: '' }],
    witnesses: '',
    reported_by_name: '',
    department_shift: '',
    equipment_items: [_blankEquipmentItem()],
    material_items: [_blankMaterialItem()],
    incident_description: '',
    cause_factors: '',
    immediate_actions: '',
    corrective_actions: '',
    injuries: false,
    medical_details: '',
  }
}

function reportToForm(r: IncidentReport): FormState {
  const allItems = r.damaged_items ?? []
  // Backward compat: items without category default to equipment
  const equipmentItems = allItems.filter(
    (d) => !d.category || d.category === 'equipment'
  )
  const materialItems = allItems.filter((d) => d.category === 'material')

  return {
    date_of_report: r.date_of_report ?? '',
    facility_site: r.facility_site ?? '',
    date_of_incident: r.date_of_incident ?? '',
    time_of_incident: r.time_of_incident ?? '',
    reported_to_mgmt_date: r.reported_to_mgmt_date ?? '',
    location_in_warehouse: r.location_in_warehouse ?? '',
    incident_types: r.incident_types ?? [],
    persons_involved: r.persons_involved.length > 0
      ? r.persons_involved
      : [{ name: '', role: '' }],
    witnesses: r.witnesses ?? '',
    reported_by_name: r.reported_by_name ?? '',
    department_shift: r.department_shift ?? '',
    equipment_items: equipmentItems.length > 0 ? equipmentItems : [_blankEquipmentItem()],
    material_items: materialItems.length > 0 ? materialItems : [_blankMaterialItem()],
    incident_description: r.incident_description ?? '',
    cause_factors: r.cause_factors ?? '',
    immediate_actions: r.immediate_actions ?? '',
    corrective_actions: r.corrective_actions ?? '',
    injuries: r.injuries,
    medical_details: r.medical_details ?? '',
  }
}

// Empty numeric inputs hold '' — the backend expects a number or null.
function blankNumbersToNull(d: DamagedItem): DamagedItem {
  return {
    ...d,
    quantity_affected: d.quantity_affected === '' ? null : d.quantity_affected,
    estimated_damage_value: d.estimated_damage_value === '' ? null : d.estimated_damage_value,
  }
}

// ─── Page ───────────────────────────────────────────────────────────

export function WarehouseIncidentPage() {
  const navigate = useNavigate()
  const params = useParams({ strict: false }) as { reportId?: string }
  const reportId = params.reportId === 'new' ? null : (params.reportId ?? null)
  const isNew = reportId === null

  const { data: report, isLoading } = useIncident(reportId)
  const createMutation = useCreateIncident()
  const updateMutation = useUpdateIncident(reportId ?? '')
  const deleteMutation = useDeleteIncident()
  const pdfMutation = useGeneratePdf(reportId ?? '')

  const [form, setForm] = useState<FormState>(blankForm)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (report) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs fetched report into form state; gated by report identity so no loop.
      setForm(reportToForm(report))
      // eslint-disable-next-line react-hooks/set-state-in-effect -- clears dirty flag when remote data arrives; paired with the setForm above.
      setDirty(false)
    }
  }, [report])

  function set<K extends keyof FormState>(field: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [field]: value }))
    setDirty(true)
  }

  function toggleIncidentType(type: IncidentType) {
    const current = form.incident_types as IncidentType[]
    const next = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type]
    set('incident_types', next)
  }

  function updatePerson(idx: number, field: keyof PersonInvolved, value: string) {
    const next = form.persons_involved.map((p, i) =>
      i === idx ? { ...p, [field]: value } : p
    )
    set('persons_involved', next)
  }

  function addPerson() {
    set('persons_involved', [...form.persons_involved, { name: '', role: '' }])
  }

  function removePerson(idx: number) {
    if (form.persons_involved.length <= 1) return
    set('persons_involved', form.persons_involved.filter((_, i) => i !== idx))
  }

  async function handleSave() {
    try {
      const mergedDamagedItems = [
        ...form.equipment_items
          .filter((d) => d.description || d.asset_id || d.product_code)
          .map((d) => {
            const { repairable, ...eqRest } = blankNumbersToNull(d)
            return {
              ...eqRest,
              category: 'equipment' as const,
              // '' is the unselected dropdown default — omit rather than
              // send it; the backend validator only accepts yes/no/tbd/None.
              ...(repairable ? { repairable } : {}),
            }
          }),
        ...form.material_items
          .filter((d) => d.description || d.product_code || d.lot_serial)
          .map((d) => {
            // Repairable doesn't apply to material damage — never send it.
            const { repairable: _repairable, ...matRest } = blankNumbersToNull(d)
            return { ...matRest, category: 'material' as const }
          }),
      ]
      const { equipment_items: _eq, material_items: _mat, ...rest } = form
      const payload = {
        ...rest,
        date_of_report: form.date_of_report || new Date().toISOString().slice(0, 10),
        date_of_incident: form.date_of_incident || undefined,
        reported_to_mgmt_date: form.reported_to_mgmt_date || undefined,
        persons_involved: form.persons_involved.filter((p) => p.name || p.role),
        damaged_items: mergedDamagedItems,
      }
      if (isNew) {
        const created = await createMutation.mutateAsync(payload)
        toast.success(`Report ${created.report_number} created.`)
        navigate({ to: '/warehouse-incidents/$reportId', params: { reportId: created.id } })
      } else {
        await updateMutation.mutateAsync(payload)
        toast.success('Report saved.')
        setDirty(false)
      }
    } catch (error) {
      toast.danger(error instanceof ApiError ? error.message : 'Save failed. Please try again.')
    }
  }

  async function handleSubmit() {
    try {
      if (dirty) await handleSave()
      await updateMutation.mutateAsync({ action: 'submit' })
      toast.success('Report submitted for sign-off.')
    } catch (error) {
      toast.danger(error instanceof ApiError ? error.message : 'Submit failed.')
    }
  }

  async function handleGeneratePdf() {
    try {
      const result = await pdfMutation.mutateAsync()
      window.open(`/api/exports/${result.file_id}`, '_blank')
    } catch (error) {
      toast.danger(error instanceof ApiError ? error.message : 'PDF generation failed.')
    }
  }

  async function handleDelete() {
    if (!reportId) return
    if (!confirm('Delete this incident report? This cannot be undone.')) return
    try {
      await deleteMutation.mutateAsync(reportId)
      toast.success('Report deleted.')
      navigate({ to: '/warehouse-incidents' })
    } catch (error) {
      toast.danger(error instanceof ApiError ? error.message : 'Delete failed.')
    }
  }

  const isAdmin = usePermission('admin')
  const isSaving = createMutation.isPending || updateMutation.isPending
  const isReadOnly = !isNew && report?.status === 'reviewed' && !isAdmin
  const canDelete = !isNew && (report?.status === 'draft' || isAdmin)
  const incidentTypes = form.incident_types
  const showEquipmentSection = incidentTypes.includes('equipment_damage')
  const showMaterialSection = incidentTypes.includes('material_damage')
  const showInjuryFields = incidentTypes.includes('personal_injury')

  if (!isNew && isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="animate-spin text-secondary" size={24} />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full overflow-auto">
      {/* Topbar */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-border-default bg-surface sticky top-0 z-10">
        <button
          type="button"
          onClick={() => navigate({ to: '/warehouse-incidents' })}
          className="text-secondary hover:text-primary transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold">
              {isNew ? 'New Incident Report' : (report?.report_number ?? 'Incident Report')}
            </h1>
            {report && <IncidentStatusChip status={report.status} />}
          </div>
          {report && (
            <p className="text-xs text-secondary mt-0.5">
              Created by {report.created_by ?? 'unknown'} on{' '}
              {report.created_at ? new Date(report.created_at).toLocaleDateString() : '—'}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {report?.pdf_file_id && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(`/api/exports/${report.pdf_file_id}`, '_blank')}
            >
              <Download size={14} className="mr-1" />
              PDF
            </Button>
          )}
          {!isNew && !isReadOnly && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleGeneratePdf}
              loading={pdfMutation.isPending}
            >
              <FileText size={14} className="mr-1" />
              {report?.pdf_file_id ? 'Regenerate PDF' : 'Generate PDF'}
            </Button>
          )}
          {!isReadOnly && dirty && (
            <Button variant="secondary" size="sm" onClick={handleSave} loading={isSaving}>
              <Save size={14} className="mr-1" />
              Save
            </Button>
          )}
          {!isNew && report?.status === 'draft' && (
            <Button variant="primary" size="sm" onClick={handleSubmit} loading={isSaving}>
              <Send size={14} className="mr-1" />
              Submit
            </Button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={handleDelete}
              className="text-secondary hover:text-danger transition-colors ml-1"
              title="Delete report"
            >
              <Trash2 size={16} />
            </button>
          )}
          {isNew && (
            <Button variant="primary" size="sm" onClick={handleSave} loading={isSaving}>
              Create Report
            </Button>
          )}
        </div>
      </div>

      {/* Form body */}
      <div className="flex-1 max-w-5xl mx-auto w-full px-6 py-6 space-y-6">
        {/* Header fields */}
        <FieldGroup>
          {!isNew && (
            <Field label="Report Number">
              <Input value={report?.report_number ?? ''} disabled />
            </Field>
          )}
          <Field label="Date of Report">
            <Input
              type="date"
              value={form.date_of_report}
              onChange={(e) => set('date_of_report', e.target.value)}
              disabled={isReadOnly}
            />
          </Field>
          <Field label="Facility / Site" colSpan={isNew ? 2 : 1}>
            <Input
              value={form.facility_site}
              onChange={(e) => set('facility_site', e.target.value)}
              placeholder="Warehouse name or address"
              disabled={isReadOnly}
            />
          </Field>
        </FieldGroup>

        {/* Section 1 */}
        <div>
          <SectionHeader label="1. INCIDENT DETAILS" />
          <SectionBody>
            <FieldGroup>
              <Field label="Date of Incident">
                <Input
                  type="date"
                  value={form.date_of_incident}
                  onChange={(e) => set('date_of_incident', e.target.value)}
                  disabled={isReadOnly}
                />
              </Field>
              <Field label="Time of Incident">
                <Input
                  type="time"
                  value={form.time_of_incident}
                  onChange={(e) => set('time_of_incident', e.target.value)}
                  disabled={isReadOnly}
                />
              </Field>
              <Field label="Reported to Mgmt (Date)">
                <Input
                  type="date"
                  value={form.reported_to_mgmt_date}
                  onChange={(e) => set('reported_to_mgmt_date', e.target.value)}
                  disabled={isReadOnly}
                />
              </Field>
            </FieldGroup>
            <Field label="Location in Warehouse (zone / aisle / bay / dock)" colSpan={3}>
              <Input
                value={form.location_in_warehouse}
                onChange={(e) => set('location_in_warehouse', e.target.value)}
                placeholder="e.g. Zone A, Aisle 3, Bay 12"
                disabled={isReadOnly}
              />
            </Field>
            <div className="space-y-2">
              <label className="text-xs text-secondary uppercase tracking-wide font-medium">
                Type of Incident (check all that apply)
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(Object.entries(INCIDENT_TYPE_LABELS) as [IncidentType, string][]).map(
                  ([key, label]) => (
                    <label
                      key={key}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Checkbox
                        checked={incidentTypes.includes(key)}
                        onChange={() => toggleIncidentType(key)}
                        disabled={isReadOnly}
                      />
                      <span className="text-sm">{label}</span>
                    </label>
                  )
                )}
              </div>
            </div>
          </SectionBody>
        </div>

        {/* Section 2 */}
        <div>
          <SectionHeader label="2. PEOPLE INVOLVED" />
          <SectionBody>
            <div className="space-y-2">
              <label className="text-xs text-secondary uppercase tracking-wide font-medium">
                Person(s) Directly Involved
              </label>
              {form.persons_involved.map((person, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_1fr_36px] gap-3 items-start">
                  <Input
                    value={person.name}
                    onChange={(e) => updatePerson(idx, 'name', e.target.value)}
                    placeholder={`Person ${idx + 1} name`}
                    disabled={isReadOnly}
                  />
                  <Input
                    value={person.role}
                    onChange={(e) => updatePerson(idx, 'role', e.target.value)}
                    placeholder="Role / Title"
                    disabled={isReadOnly}
                  />
                  {!isReadOnly && form.persons_involved.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => removePerson(idx)}
                      className="h-8 w-8 flex items-center justify-center rounded text-secondary hover:text-danger hover:bg-danger/10 transition-colors"
                      title="Remove person"
                    >
                      <Trash2 size={14} />
                    </button>
                  ) : (
                    <div />
                  )}
                </div>
              ))}
              {!isReadOnly && (
                <Button variant="ghost" size="sm" onClick={addPerson} className="mt-1">
                  <Plus size={14} className="mr-1" />
                  Add person
                </Button>
              )}
            </div>
            <Field label="Witness(es) — Name(s) & Contact" colSpan={3}>
              <Textarea
                value={form.witnesses}
                onChange={(e) => set('witnesses', e.target.value)}
                placeholder="Witness names and contact information"
                rows={2}
                disabled={isReadOnly}
              />
            </Field>
            <FieldGroup>
              <Field label="Reported By (Name)" colSpan={2}>
                <Input
                  value={form.reported_by_name}
                  onChange={(e) => set('reported_by_name', e.target.value)}
                  placeholder="Name of person filing the report"
                  disabled={isReadOnly}
                />
              </Field>
              <Field label="Department / Shift">
                <Input
                  value={form.department_shift}
                  onChange={(e) => set('department_shift', e.target.value)}
                  placeholder="e.g. Receiving / Day"
                  disabled={isReadOnly}
                />
              </Field>
            </FieldGroup>
          </SectionBody>
        </div>

        {/* Section 3A — Equipment Damaged (only when equipment_damage checked) */}
        {showEquipmentSection && (
          <div>
            <SectionHeader label="3A. EQUIPMENT DAMAGED" />
            <SectionBody>
              <DamagedItemsEditor
                variant="equipment"
                items={form.equipment_items}
                onChange={(items) => set('equipment_items', items)}
                readOnly={isReadOnly}
              />
            </SectionBody>
          </div>
        )}

        {/* Section 3B — Material / Stock Damaged (only when material_damage checked) */}
        {showMaterialSection && (
          <div>
            <SectionHeader label="3B. MATERIAL / STOCK DAMAGED" />
            <SectionBody>
              <DamagedItemsEditor
                variant="material"
                items={form.material_items}
                onChange={(items) => set('material_items', items)}
                readOnly={isReadOnly}
              />
            </SectionBody>
          </div>
        )}

        {/* Section 4 */}
        <div>
          <SectionHeader label="4. INCIDENT DESCRIPTION & CAUSE" />
          <SectionBody>
            <Field label="Describe What Happened (sequence of events)" colSpan={3}>
              <Textarea
                value={form.incident_description}
                onChange={(e) => set('incident_description', e.target.value)}
                placeholder="Describe the sequence of events leading to the incident…"
                rows={4}
                disabled={isReadOnly}
              />
            </Field>
            <Field label="Cause / Contributing Factors" colSpan={3}>
              <Textarea
                value={form.cause_factors}
                onChange={(e) => set('cause_factors', e.target.value)}
                placeholder="Root cause and contributing factors…"
                rows={3}
                disabled={isReadOnly}
              />
            </Field>
          </SectionBody>
        </div>

        {/* Section 5 */}
        <div>
          <SectionHeader label="5. ACTIONS & INJURIES" />
          <SectionBody>
            <Field label="Immediate Actions Taken" colSpan={3}>
              <Textarea
                value={form.immediate_actions}
                onChange={(e) => set('immediate_actions', e.target.value)}
                placeholder="Actions taken immediately following the incident…"
                rows={3}
                disabled={isReadOnly}
              />
            </Field>
            <Field label="Corrective / Preventive Actions Recommended" colSpan={3}>
              <Textarea
                value={form.corrective_actions}
                onChange={(e) => set('corrective_actions', e.target.value)}
                placeholder="Long-term corrective or preventive measures…"
                rows={3}
                disabled={isReadOnly}
              />
            </Field>
            {showInjuryFields && (
              <>
                <div className="flex items-center gap-3">
                  <Toggle
                    checked={form.injuries}
                    onChange={(e) => set('injuries', e.target.checked)}
                    disabled={isReadOnly}
                  />
                  <span className="text-sm">Were there injuries?</span>
                </div>
                {form.injuries && (
                  <Field label="Medical Attention Required / Details" colSpan={3}>
                    <Textarea
                      value={form.medical_details}
                      onChange={(e) => set('medical_details', e.target.value)}
                      placeholder="Describe injuries and medical attention required…"
                      rows={3}
                      disabled={isReadOnly}
                    />
                  </Field>
                )}
              </>
            )}
          </SectionBody>
        </div>

        {/* Section 6 — Sign-off */}
        {!isNew && (
          <div>
            <SectionHeader label="6. SIGN-OFF" />
            <SectionBody>
              {report?.status === 'draft' ? (
                <p className="text-sm text-secondary">
                  Submit the report to enable sign-off.
                </p>
              ) : (
                <SignOffPanel report={report!} />
              )}
            </SectionBody>
          </div>
        )}

        {/* Section 7 — Linked Records */}
        {!isNew && report && (
          <div>
            <SectionHeader label="7. LINKED RECORDS" />
            <SectionBody>
              <LinkedDiscrepancyPanel report={report} isReadOnly={isReadOnly} />
            </SectionBody>
          </div>
        )}

        {/* Bottom save bar */}
        {!isReadOnly && dirty && (
          <div className="flex justify-end py-4">
            <Button variant="primary" size="sm" onClick={handleSave} loading={isSaving}>
              <Save size={14} className="mr-1" />
              Save Changes
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Linked records (cross-module: Inventory Discrepancy Reports) ──

function LinkedDiscrepancyPanel({
  report,
  isReadOnly,
}: {
  report: IncidentReport
  isReadOnly: boolean
}) {
  const [draftId, setDraftId] = useState('')
  const link = useLinkDiscrepancyReport(report.id)
  const unlink = useUnlinkDiscrepancyReport(report.id)

  if (report.inventory_discrepancy_report_id) {
    return (
      <div className="flex items-center gap-3">
        <a
          href={`/inventory-discrepancy-reports/${report.inventory_discrepancy_report_id}`}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-link hover:underline"
        >
          View linked Discrepancy Report ↗
        </a>
        {!isReadOnly && (
          <Button
            variant="ghost"
            size="sm"
            loading={unlink.isPending}
            onClick={() =>
              unlink.mutate(undefined, {
                onSuccess: () => toast.success('Unlinked'),
                onError: (err) =>
                  toast.danger(`Unlink failed${err instanceof Error ? `: ${err.message}` : ''}`),
              })
            }
          >
            Unlink
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <Input
        value={draftId}
        onChange={(e) => setDraftId(e.target.value)}
        placeholder="Discrepancy Report ID (UUID)"
        disabled={isReadOnly}
        className="max-w-xs"
      />
      <Button
        variant="secondary"
        size="sm"
        disabled={isReadOnly || !draftId.trim()}
        loading={link.isPending}
        onClick={() =>
          link.mutate(draftId.trim(), {
            onSuccess: () => {
              toast.success('Linked')
              setDraftId('')
            },
            onError: (err) =>
              toast.danger(`Link failed${err instanceof Error ? `: ${err.message}` : ''}`),
          })
        }
      >
        Link
      </Button>
    </div>
  )
}
